
import { useEffect, useCallback, useRef } from 'react';

interface PerformanceMetrics {
  fcp?: number; // First Contentful Paint
  lcp?: number; // Largest Contentful Paint
  fid?: number; // First Input Delay
  cls?: number; // Cumulative Layout Shift
  ttfb?: number; // Time to First Byte
}

// Throttle function to prevent spam
const throttle = (func: Function, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  let lastExecTime = 0;
  return (...args: any[]) => {
    const currentTime = Date.now();
    
    if (currentTime - lastExecTime > delay) {
      func(...args);
      lastExecTime = currentTime;
    } else {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func(...args);
        lastExecTime = Date.now();
      }, delay - (currentTime - lastExecTime));
    }
  };
};

export const usePerformanceMonitor = () => {
  const clsValueRef = useRef(0);
  const lastReportTime = useRef(0);
  const reportedMetrics = useRef(new Set<string>());

  const reportMetric = useCallback(
    throttle((metric: PerformanceMetrics) => {
      // Avoid duplicate reports
      const metricKey = Object.keys(metric)[0];
      if (reportedMetrics.current.has(metricKey)) return;
      
      // Only report in development for debugging
      if (import.meta.env.DEV) {
        console.log('Performance Metric:', metric);
      }
      
      // Mark as reported to prevent duplicates
      reportedMetrics.current.add(metricKey);
      
      // In production, send to analytics (implement your analytics here)
      if (import.meta.env.PROD && typeof window !== 'undefined') {
        // Example: Send to Google Analytics
        if ((window as any).gtag) {
          (window as any).gtag('event', 'performance_metric', {
            metric_name: metricKey,
            metric_value: Math.round(Object.values(metric)[0] as number),
            custom_parameter: 'core_web_vitals'
          });
        }
      }
    }, 1000), // Throttle to max 1 report per second
    []
  );

  useEffect(() => {
    // Skip if not in browser
    if (typeof window === 'undefined') return;

    let observers: PerformanceObserver[] = [];

    try {
      // Measure FCP (First Contentful Paint) - only once
      const fcpObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.name === 'first-contentful-paint' && !reportedMetrics.current.has('fcp')) {
            reportMetric({ fcp: entry.startTime });
          }
        });
      });
      fcpObserver.observe({ entryTypes: ['paint'] });
      observers.push(fcpObserver);

      // Measure LCP (Largest Contentful Paint) - throttled
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        if (entries.length > 0) {
          const lastEntry = entries[entries.length - 1];
          reportMetric({ lcp: lastEntry.startTime });
        }
      });
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      observers.push(lcpObserver);

      // Measure FID (First Input Delay) - only once
      const fidObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry: any) => {
          if (!reportedMetrics.current.has('fid')) {
            reportMetric({ fid: entry.processingStart - entry.startTime });
          }
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });
      observers.push(fidObserver);

      // Measure CLS (Cumulative Layout Shift) - heavily throttled
      const clsObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValueRef.current += entry.value;
          }
        });
        
        // Only report CLS every 3 seconds to prevent spam
        const now = Date.now();
        if (now - lastReportTime.current > 3000) {
          reportMetric({ cls: clsValueRef.current });
          lastReportTime.current = now;
        }
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
      observers.push(clsObserver);

      // Measure TTFB (Time to First Byte) - only once
      if ('navigation' in performance && !reportedMetrics.current.has('ttfb')) {
        const navigationEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        if (navigationEntry) {
          const ttfb = navigationEntry.responseStart - navigationEntry.requestStart;
          reportMetric({ ttfb });
        }
      }

    } catch (error) {
      console.warn('Performance monitoring not supported:', error);
    }

    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [reportMetric]);

  const measureCustomMetric = useCallback((name: string, startTime: number) => {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    if (import.meta.env.DEV) {
      console.log(`Custom Metric ${name}:`, Math.round(duration), 'ms');
    }
    
    return duration;
  }, []);

  return { measureCustomMetric };
};

export default usePerformanceMonitor;
